import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SessionTracker from "@/components/SessionTracker";
import {
  ArrowLeft, ClipboardCheck, Wallet, Save, MessageCircle, ChevronDown,
  MapPin, Bell, FileText, RefreshCcw, MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { buildPacienteWhatsApp } from "@/lib/whatsapp";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

const ESTADO_CRM_META = {
  nuevo:                { label: "Nuevo",              className: "bg-neutral-100 text-neutral-700" },
  consulta_agendada:    { label: "Consulta agendada",  className: "bg-amber-100 text-amber-700" },
  consulta_confirmada:  { label: "Consulta confirmada", className: "bg-blue-100 text-blue-700" },
  en_tratamiento:       { label: "En tratamiento",     className: "bg-orange-100 text-orange-700" },
  en_seguimiento:       { label: "En seguimiento",     className: "bg-teal-100 text-teal-700" },
  alta:                 { label: "Alta",               className: "bg-emerald-100 text-emerald-700" },
  inactivo:             { label: "Inactivo",           className: "bg-neutral-200 text-neutral-600" },
  abandonado:           { label: "Abandonó",           className: "bg-red-100 text-red-700" },
};

export default function PacienteDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const cfg = useConfig();
  const [p, setP] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [comunicaciones, setComunicaciones] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [tab, setTab] = useState("resumen");

  const load = useCallback(async () => {
    try {
      const [pa, t, pg, pr, com] = await Promise.all([
        api.get(`/pacientes/${id}`),
        api.get("/turnos", { params: { paciente_id: id } }),
        api.get("/pagos", { params: { paciente_id: id } }),
        api.get("/profesionales"),
        api.get("/comunicaciones", { params: { paciente_id: id } }),
      ]);
      setP(pa.data);
      setTurnos(t.data);
      setPagos(pg.data);
      setProfesionales(pr.data);
      setComunicaciones(com.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (!p) return <div className="p-10 text-neutral-500">Cargando…</div>;

  const prof = profesionales.find((x) => x.id === p.profesional_id);
  const estadoCrm = ESTADO_CRM_META[p.estado_crm] || ESTADO_CRM_META.nuevo;
  const testLabel = cfg.test_inicial_nombre ? `Test ${cfg.test_inicial_nombre}` : "Evaluación inicial";

    const logCom = async (accion) => {
      try {
        await api.post("/comunicaciones", {
          paciente_id: p.id,
          tipo: "whatsapp",
          accion,
          contenido: `Envío WhatsApp — ${accion}`,
        });
        load();
      } catch (err) {
        console.warn("No se pudo registrar la comunicación:", err);
      }
    };

  const openWa = (accion, extras = {}) => {
    const url = buildPacienteWhatsApp(p, accion, cfg, extras);
    logCom(accion);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <Link to="/pacientes" className="btn-ghost-pill mb-4 -ml-2 text-sm" data-testid="volver-pacientes">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver
      </Link>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-2">
            Ficha ·
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${estadoCrm.className}`}>
              {estadoCrm.label}
            </span>
          </span>
        }
        title={`${p.nombre} ${p.apellido}`}
        description={`${p.dni ? "CI " + p.dni + " · " : ""}${p.telefono || "Sin teléfono"} · ${p.email || "Sin email"}`}
        action={
          <div className="flex items-center gap-3">
            <WhatsAppMenu paciente={p} onAction={openWa} />
            <div className="text-right hidden sm:block">
              <div className="overline">Progreso</div>
              <SessionTracker completed={p.sesiones_completadas} total={p.sesiones_totales} size="lg" />
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <StatBox
          testId="detalle-costo"
          icon={Wallet}
          label="Costo del tratamiento"
          value={fmtGs(p.costo_tratamiento)}
        />
        <StatBox
          testId="detalle-pagado"
          icon={Wallet}
          accent="sage"
          label="Total pagado"
          value={fmtGs(p.total_pagado)}
        />
        <StatBox
          testId="detalle-saldo"
          icon={Wallet}
          accent={p.saldo_pendiente > 0 ? "terracotta" : "sage"}
          label="Saldo pendiente"
          value={fmtGs(p.saldo_pendiente)}
        />
      </div>

      <div className="flex gap-2 border-b border-neutral-200 mb-6 overflow-x-auto">
        {[
          { v: "resumen", l: "Resumen" },
          { v: "origen", l: testLabel },
          { v: "turnos", l: `Turnos (${turnos.length})` },
          { v: "pagos", l: `Pagos (${pagos.length})` },
          { v: "comunicaciones", l: `Comunicaciones (${comunicaciones.length})` },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setTab(o.v)}
            data-testid={`tab-${o.v}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === o.v
                ? "border-[hsl(var(--sage))] text-[hsl(var(--sage-hover))]"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      {tab === "resumen" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="card-clinic p-6">
            <div className="overline mb-3">Datos personales</div>
            <dl className="text-sm space-y-2">
              <Row k="Nombre completo" v={`${p.nombre} ${p.apellido}`} />
              <Row k="CI" v={p.dni || "—"} />
              <Row k="Fecha de nacimiento" v={p.fecha_nacimiento || "—"} />
              <Row k="Teléfono" v={p.telefono || "—"} />
              <Row k="Email" v={p.email || "—"} />
              <Row k="Dirección" v={p.direccion || "—"} />
              <Row k="Profesional a cargo" v={prof?.name || "Sin asignar"} />
              <Row k="Estado" v={p.estado} />
            </dl>
          </div>
          <div className="card-clinic p-6">
            <div className="overline mb-3">Notas</div>
            <p className="text-sm text-neutral-600 whitespace-pre-wrap">
              {p.notas || "Sin notas cargadas."}
            </p>
          </div>
        </div>
      )}

      {tab === "origen" && <OrigenPanel paciente={p} onSaved={load} canEdit={user?.role !== "recepcionista"} />}

      {tab === "turnos" && (
        <div className="card-clinic p-2">
          {turnos.length === 0 ? (
            <div className="text-sm text-neutral-500 py-14 text-center">No hay turnos cargados.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50/50">
                  {["Fecha", "Hora", "Tipo", "Profesional", "Estado"].map((h, i) => (
                    <th key={i} className="overline text-left px-4 py-3 text-neutral-500">
                      {h}
                    </th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {turnos.map((t) => (
                  <tr key={t.id} className="border-b border-neutral-200 row-hover">
                    <td className="px-4 py-3 font-medium text-neutral-800">{t.fecha}</td>
                    <td className="px-4 py-3 font-mono">{t.hora}</td>
                    <td className="px-4 py-3">
                      {t.tipo === "origen" ? "Test ORIGEN" : t.tipo === "sesion" ? "Sesión" : t.tipo}
                    </td>
                    <td className="px-4 py-3">{t.profesional_nombre}</td>
                    <td className="px-4 py-3">
                      <TurnoEstadoSelect turno={t} onChanged={load} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "pagos" && <PagosPanel paciente={p} pagos={pagos} onSaved={load} />}

      {tab === "comunicaciones" && (
        <ComunicacionesPanel comunicaciones={comunicaciones} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// WhatsApp actions dropdown
// -----------------------------------------------------------------------------
function WhatsAppMenu({ paciente, onAction }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const acciones = [
    { key: "chat",         icon: MessageCircle, label: "Iniciar / seguir chat" },
    { key: "recordatorio", icon: Bell,          label: "Enviar recordatorio" },
    { key: "ubicacion",    icon: MapPin,        label: "Enviar ubicación" },
    { key: "indicacion",   icon: ClipboardCheck,label: "Enviar indicaciones" },
    { key: "documento",    icon: FileText,      label: "Pedir un documento" },
    { key: "seguimiento",  icon: RefreshCcw,    label: "Seguimiento / reengagement" },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-testid="wa-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-medium px-4 py-2.5 text-sm transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-neutral-200 bg-white shadow-xl overflow-hidden z-30">
          {!paciente.telefono && (
            <div className="px-4 py-2 text-xs text-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/10 border-b border-neutral-200">
              El paciente no tiene teléfono cargado — se abrirá WhatsApp para elegir contacto.
            </div>
          )}
          {acciones.map((a) => (
            <button
              key={a.key}
              data-testid={`wa-action-${a.key}`}
              onClick={() => {
                setOpen(false);
                onAction(a.key);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              <a.icon className="w-4 h-4 text-neutral-500" />
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ComunicacionesPanel({ comunicaciones }) {
  const ACCION_LABEL = {
    recordatorio: "Recordatorio",
    ubicacion: "Ubicación",
    indicacion: "Indicaciones",
    documento: "Pedido de documento",
    seguimiento: "Seguimiento",
    chat: "Chat / mensaje",
  };
  return (
    <div className="card-clinic p-6 md:p-8">
      <div className="flex items-center gap-2 mb-5">
        <MessagesSquare className="w-4 h-4 text-neutral-500" />
        <h3 className="text-lg font-bold tracking-tight">Historial de comunicaciones</h3>
      </div>
      {comunicaciones.length === 0 ? (
        <div className="text-sm text-neutral-500 py-8 text-center border border-dashed border-neutral-200 rounded-xl">
          Aún no hay comunicaciones registradas con este paciente.
        </div>
      ) : (
        <ol className="relative border-l border-neutral-200 ml-3 space-y-5">
          {comunicaciones.map((c) => (
            <li key={c.id} className="ml-5" data-testid={`com-item-${c.id}`}>
              <span className="absolute -left-2 w-4 h-4 rounded-full border-2 border-white bg-[#25D366]" />
              <div className="flex flex-wrap items-baseline gap-2 mb-1">
                <span className="text-sm font-semibold text-neutral-900">
                  {ACCION_LABEL[c.accion] || (c.tipo || "Comunicación")}
                </span>
                <span className="text-xs text-neutral-400">·</span>
                <span className="text-xs text-neutral-500">
                  {new Date(c.created_at).toLocaleString("es-PY")}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                Vía {c.tipo || "whatsapp"} · {c.registrado_por_nombre || "—"}
              </div>
              {c.contenido && (
                <p className="text-sm text-neutral-700 mt-1.5">{c.contenido}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent = "neutral", testId }) {
  const accents = {
    sage: "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]",
    terracotta: "bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className="card-clinic p-6" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="overline mt-5 mb-1">{label}</div>
      <div className="text-2xl font-bold tracking-tight text-neutral-900">{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-neutral-100 last:border-0">
      <dt className="text-neutral-500">{k}</dt>
      <dd className="text-neutral-900 font-medium text-right">{v}</dd>
    </div>
  );
}

function TurnoEstadoSelect({ turno, onChanged }) {
  const [val, setVal] = useState(turno.estado);
  const change = async (v) => {
    setVal(v);
    try {
      await api.patch(`/turnos/${turno.id}`, { estado: v });
      toast.success("Estado actualizado");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <select
      value={val}
      onChange={(e) => change(e.target.value)}
      className="input-clinic max-w-[160px] text-sm py-1.5"
      data-testid={`turno-estado-${turno.id}`}
    >
      <option value="programado">Programado</option>
      <option value="asistio">Asistió</option>
      <option value="ausente">Ausente</option>
      <option value="cancelado">Cancelado</option>
    </select>
  );
}

function OrigenPanel({ paciente, onSaved, canEdit }) {
  const [form, setForm] = useState({
    motivo_consulta: paciente.origen_test?.motivo_consulta || "",
    antecedentes: paciente.origen_test?.antecedentes || "",
    observaciones: paciente.origen_test?.observaciones || "",
    diagnostico_inicial: paciente.origen_test?.diagnostico_inicial || "",
    plan_tratamiento: paciente.origen_test?.plan_tratamiento || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.observaciones.trim()) {
      toast.error("Las observaciones son obligatorias");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/pacientes/${paciente.id}/origen-test`, form);
      toast.success("Evaluación ORIGEN guardada");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-clinic p-6 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Evaluación inicial
          </div>
          <h3 className="text-2xl font-bold tracking-tight">Test ORIGEN</h3>
          {paciente.origen_test && (
            <p className="text-xs text-neutral-500 mt-1">
              Última evaluación por {paciente.origen_test.evaluado_por_nombre} —{" "}
              {new Date(paciente.origen_test.fecha).toLocaleString("es-PY")}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FieldArea
          label="Motivo de consulta"
          value={form.motivo_consulta}
          onChange={(v) => set("motivo_consulta", v)}
          disabled={!canEdit}
          testId="origen-motivo"
        />
        <FieldArea
          label="Antecedentes relevantes"
          value={form.antecedentes}
          onChange={(v) => set("antecedentes", v)}
          disabled={!canEdit}
          testId="origen-antecedentes"
        />
        <div className="md:col-span-2">
          <FieldArea
            label="Observaciones del profesional *"
            value={form.observaciones}
            onChange={(v) => set("observaciones", v)}
            disabled={!canEdit}
            rows={5}
            testId="origen-observaciones"
          />
        </div>
        <FieldArea
          label="Diagnóstico inicial / Hipótesis"
          value={form.diagnostico_inicial}
          onChange={(v) => set("diagnostico_inicial", v)}
          disabled={!canEdit}
          testId="origen-diagnostico"
        />
        <FieldArea
          label="Plan de tratamiento"
          value={form.plan_tratamiento}
          onChange={(v) => set("plan_tratamiento", v)}
          disabled={!canEdit}
          testId="origen-plan"
        />
      </div>

      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button data-testid="origen-guardar" onClick={save} disabled={saving} className="btn-primary-pill">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Guardando…" : "Guardar evaluación"}
          </button>
        </div>
      )}
    </div>
  );
}

function FieldArea({ label, value, onChange, rows = 3, disabled, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1.5">{label}</label>
      <textarea
        data-testid={testId}
        rows={rows}
        disabled={disabled}
        className="input-clinic"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function PagosPanel({ paciente, pagos, onSaved }) {
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("efectivo");
  const [concepto, setConcepto] = useState("");
  const [loading, setLoading] = useState(false);

  const registrar = async () => {
    const m = Number(monto);
    if (!m || m <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setLoading(true);
    try {
      await api.post("/pagos", {
        paciente_id: paciente.id,
        monto: m,
        metodo,
        concepto: concepto || undefined,
      });
      toast.success("Pago registrado");
      setMonto("");
      setConcepto("");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div className="card-clinic p-6 md:col-span-1">
        <div className="overline mb-3 flex items-center gap-2">
          <Wallet className="w-3.5 h-3.5" /> Nuevo pago parcial
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Monto</label>
            <input
              data-testid="nuevo-pago-monto"
              type="number"
              className="input-clinic"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Método</label>
            <select
              data-testid="nuevo-pago-metodo"
              className="input-clinic"
              value={metodo}
              onChange={(e) => setMetodo(e.target.value)}
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Concepto (opcional)</label>
            <input
              data-testid="nuevo-pago-concepto"
              className="input-clinic"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Ej: Seña, sesión 3, etc."
            />
          </div>
          <button
            data-testid="nuevo-pago-guardar"
            onClick={registrar}
            disabled={loading}
            className="btn-primary-pill w-full"
          >
            Registrar pago
          </button>
        </div>
      </div>

      <div className="card-clinic md:col-span-2 overflow-hidden">
        <div className="overline p-6 pb-3">Historial de pagos</div>
        {pagos.length === 0 ? (
          <div className="text-sm text-neutral-500 py-10 text-center">Sin pagos registrados aún.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/50">
                {["Fecha", "Monto", "Método", "Concepto", "Registrado por"].map((h, i) => (
                  <th key={i} className="overline text-left px-4 py-3 text-neutral-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagos.map((pg) => (
                <tr key={pg.id} className="border-b border-neutral-200 row-hover">
                  <td className="px-4 py-3">{pg.fecha}</td>
                  <td className="px-4 py-3 font-semibold text-[hsl(var(--sage-hover))]">
                    {fmtGs(pg.monto)}
                  </td>
                  <td className="px-4 py-3 capitalize">{pg.metodo}</td>
                  <td className="px-4 py-3 text-neutral-600">{pg.concepto || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{pg.registrado_por_nombre || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
